import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'

/**
 * App UI language. Keyed by the ENGLISH string itself (gettext style):
 * components call t('New week opens') and each dictionary maps that
 * English to its translation. Missing entries fall back to the English
 * key, so partial dictionaries degrade gracefully instead of breaking.
 *
 * The language set mirrors the living rulebooks on jordandraper.com.
 * User-written content (stories, steps, dish titles) is never
 * translated, and the word games' words/definitions are English by
 * design until per-language content pools exist (scoped for 2.1).
 *
 * Choice precedence: what the user picked this session > the saved
 * profiles.language > device language > English. Picking a language
 * writes localStorage (instant, works signed-out) and, when signed in,
 * profiles.language so the choice follows the account.
 */
export type Lang = 'en' | 'de' | 'es' | 'fr' | 'it' | 'ja'

export const LANGS: { code: Lang; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'ja', label: '日本語' },
]

type Dict = Record<string, string>

const loaders: Record<Exclude<Lang, 'en'>, () => Promise<{ dict: Dict }>> = {
  de: () => import('./dictionaries/de'),
  es: () => import('./dictionaries/es'),
  fr: () => import('./dictionaries/fr'),
  it: () => import('./dictionaries/it'),
  ja: () => import('./dictionaries/ja'),
}

function deviceLang(): Lang {
  const n = (typeof navigator !== 'undefined' ? navigator.language : 'en')
    .slice(0, 2).toLowerCase()
  return (['de', 'es', 'fr', 'it', 'ja'].includes(n) ? n : 'en') as Lang
}

const STORE_KEY = 'jdg.lang'

function storedLang(): Lang | null {
  try {
    const v = localStorage.getItem(STORE_KEY)
    return v && ['en', 'de', 'es', 'fr', 'it', 'ja'].includes(v) ? v as Lang : null
  } catch { return null }
}

interface I18nCtx {
  lang: Lang
  t: (key: string, vars?: Record<string, string | number>) => string
  setLang: (l: Lang) => void
}

/**
 * Interpolate {vars} into a key. Shared by the provider and the default
 * context so a component that somehow reads the default still renders
 * "6d left" rather than the literal "{n}d left" — the fallback loses
 * translations, which is the point, but it must not lose VALUES.
 * (Vite HMR can leave a component bound to the default after hot-swapping
 * this module, which is exactly how that used to surface in dev.)
 */
function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s
  let out = s
  for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v))
  return out
}

const Ctx = createContext<I18nCtx>({
  lang: 'en',
  t: (k, vars) => interpolate(k, vars),
  setLang: () => {},
})

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { session, profile } = useAuth()
  const [lang, setLangState] = useState<Lang>(() => storedLang() ?? deviceLang())
  const [dict, setDict] = useState<Dict | null>(null)
  const chosenThisSession = useRef(false)

  // The saved account choice wins once the profile loads, unless the
  // user has just picked something on this device in this session.
  useEffect(() => {
    const saved = profile?.language as Lang | null | undefined
    if (saved && !chosenThisSession.current) setLangState(saved)
  }, [profile?.language])

  useEffect(() => {
    document.documentElement.lang = lang
    try { localStorage.setItem(STORE_KEY, lang) } catch { /* private mode */ }
    if (lang === 'en') { setDict(null); return }
    let cancelled = false
    loaders[lang]().then(m => { if (!cancelled) setDict(m.dict) })
      .catch(() => { /* chunk fetch failed offline — English fallback */ })
    return () => { cancelled = true }
  }, [lang])

  const setLang = useCallback((l: Lang) => {
    chosenThisSession.current = true
    setLangState(l)
    if (session?.user?.id) {
      void supabase.from('profiles')
        .update({ language: l })
        .eq('id', session.user.id)
        .then(() => { /* fire and forget; localStorage already holds it */ })
    }
  }, [session?.user?.id])

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) =>
      interpolate((dict && dict[key]) || key, vars),
    [dict])

  return <Ctx.Provider value={{ lang, t, setLang }}>{children}</Ctx.Provider>
}

export function useI18n(): I18nCtx {
  return useContext(Ctx)
}

/** Shorthand for components that only need the translate function. */
export function useT(): I18nCtx['t'] {
  return useContext(Ctx).t
}
